import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Star, Save, Shuffle, Plus, X, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * FeaturedAuthorsPanel — admin tool to manage the "Authors we're featuring"
 * carousel at the bottom of the public /about page.
 *
 * Data flow:
 *   - GET  /api/admin/featured-authors           → current selection + available author groups
 *   - PUT  /api/admin/featured-authors           → replace selection (1-6 entries)
 *   - POST /api/admin/featured-authors/rotate-now → force a fresh rotation
 *
 * Auto-rotate: when "rotate monthly" is on, the backend reshuffles the
 * featured set on the 1st of each month using a deterministic seed (so it
 * stays stable for the whole month and never thrashes between requests).
 */
const FeaturedAuthorsPanel = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]); // [{name, photo_url, tagline, primary_book_id}]
  const [rotateMonthly, setRotateMonthly] = useState(false);
  const [seedMonth, setSeedMonth] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const token = localStorage.getItem('token');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/featured-authors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Defensive: legacy production rows may have null/undefined `name` or
      // missing primary_book_id. Filtering them here keeps every downstream
      // render call (`a.name.split(...)`, `selectedNames.has(a.name)`, etc.)
      // safe and prevents the white-screen-on-save symptom.
      const safeAvailable = (res.data?.available_authors || []).filter(
        (a) => a && typeof a.name === 'string' && a.name.trim() && a.primary_book_id,
      );
      const safeAuthors = (res.data?.authors || []).filter(
        (a) => a && typeof a.name === 'string' && a.name.trim() && a.primary_book_id,
      );
      setAvailable(safeAvailable);
      setSelected(safeAuthors);
      setRotateMonthly(!!res.data?.rotate_monthly);
      setSeedMonth(res.data?.rotation_seed_month || null);
      setUpdatedAt(res.data?.updated_at || null);
    } catch (e) {
      toast({
        title: 'Could not load featured authors',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const selectedNames = useMemo(() => new Set(selected.map((s) => s.name)), [selected]);

  const addAuthor = (a) => {
    if (selected.length >= 6) {
      toast({ title: 'Maximum 6 featured authors', variant: 'destructive' });
      return;
    }
    if (selectedNames.has(a.name)) return;
    setSelected((cur) => [
      ...cur,
      {
        name: a.name,
        photo_url: a.photo_url || null,
        tagline: `${a.book_count} book${a.book_count === 1 ? '' : 's'} on Seven Chapter Books`,
        primary_book_id: a.primary_book_id,
      },
    ]);
  };

  const removeAuthor = (name) => setSelected((cur) => cur.filter((a) => a.name !== name));

  const moveAuthor = (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= selected.length) return;
    setSelected((cur) => {
      const copy = cur.slice();
      const [it] = copy.splice(idx, 1);
      copy.splice(next, 0, it);
      return copy;
    });
  };

  const updateField = (idx, field, value) => {
    setSelected((cur) => cur.map((a, i) => (i === idx ? { ...a, [field]: value } : a)));
  };

  const handleSave = async () => {
    if (selected.length === 0) {
      toast({ title: 'Pick at least one author', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await axios.put(
        `${API}/admin/featured-authors`,
        { rotate_monthly: rotateMonthly, authors: selected },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: 'Featured authors saved' });
      await fetchData();
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRotateNow = async () => {
    if (!window.confirm('Force a fresh random rotation now? This replaces the current selection with a new one drawn from all available authors.')) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/featured-authors/rotate-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const fresh = res.data?.authors || [];
      setSelected(fresh);
      setRotateMonthly(true);
      toast({ title: `Rotated to ${fresh.length} new author${fresh.length === 1 ? '' : 's'}`, description: 'Click Save to publish.' });
    } catch (e) {
      toast({
        title: 'Rotate failed',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden mt-8" data-testid="featured-authors-panel">
      <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-400" />
            Featured Authors
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Authors highlighted on the public <span className="text-fuchsia-300">/about</span> page. Pick 1–6.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline" size="sm"
            onClick={fetchData} disabled={loading || busy}
            className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            data-testid="featured-authors-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Reload
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={handleRotateNow} disabled={busy}
            className="bg-white/5 border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            data-testid="featured-authors-rotate-now"
          >
            <Shuffle className="h-3.5 w-3.5 mr-1.5" /> Rotate now
          </Button>
          <Button
            size="sm" onClick={handleSave} disabled={busy}
            className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
            data-testid="featured-authors-save"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save
          </Button>
        </div>
      </div>

      <div className="p-6 grid md:grid-cols-2 gap-8">
        {/* Selected */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Currently featured ({selected.length}/6)</h3>
            <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer" data-testid="featured-authors-rotate-toggle">
              <Switch checked={rotateMonthly} onCheckedChange={setRotateMonthly} />
              <span>Auto-rotate monthly</span>
            </label>
          </div>
          {seedMonth && rotateMonthly && (
            <p className="text-xs text-amber-400 mb-3">Current rotation seed: <span className="font-mono">{seedMonth}</span> — will refresh next month.</p>
          )}
          {updatedAt && (
            <p className="text-xs text-gray-500 mb-3">Last saved: {new Date(updatedAt).toLocaleString()}</p>
          )}

          {selected.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No featured authors yet. Pick from the right column →</p>
          ) : (
            <ul className="space-y-2">
              {selected.map((a, idx) => (
                <li key={a.name + idx} className="bg-white/5 border border-white/10 rounded-lg p-3" data-testid={`featured-author-selected-${idx}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-fuchsia-500/20 text-fuchsia-200 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {a.name?.split(/\s+/).slice(0, 2).map((s) => s[0]).join('').toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold truncate">{a.name}</p>
                      <input
                        type="text" value={a.tagline || ''}
                        onChange={(e) => updateField(idx, 'tagline', e.target.value)}
                        placeholder="Tagline (optional, e.g. Author of 3 novels)"
                        className="w-full mt-1 bg-zinc-900 border border-white/10 rounded text-xs text-gray-200 px-2 py-1"
                        data-testid={`featured-author-tagline-${idx}`}
                      />
                      <input
                        type="text" value={a.photo_url || ''}
                        onChange={(e) => updateField(idx, 'photo_url', e.target.value)}
                        placeholder="Photo URL (optional)"
                        className="w-full mt-1 bg-zinc-900 border border-white/10 rounded text-xs text-gray-400 px-2 py-1"
                        data-testid={`featured-author-photo-${idx}`}
                      />
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button onClick={() => moveAuthor(idx, -1)} disabled={idx === 0} className="text-xs text-gray-400 hover:text-white disabled:opacity-30 px-1.5">↑</button>
                      <button onClick={() => moveAuthor(idx, 1)} disabled={idx === selected.length - 1} className="text-xs text-gray-400 hover:text-white disabled:opacity-30 px-1.5">↓</button>
                      <button
                        onClick={() => removeAuthor(a.name)}
                        className="text-red-400 hover:text-red-300"
                        title="Remove"
                        data-testid={`featured-author-remove-${idx}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Available */}
        <div>
          <h3 className="text-sm font-semibold text-white mb-3">All authors ({available.length})</h3>
          {available.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No authors found in your books collection yet.</p>
          ) : (
            <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-2">
              {available.map((a) => {
                const isSelected = selectedNames.has(a.name);
                return (
                  <li
                    key={a.name}
                    className={`bg-white/5 border rounded-lg p-3 flex items-center gap-3 ${isSelected ? 'border-fuchsia-500/40 opacity-60' : 'border-white/10'}`}
                    data-testid={`featured-author-available-${a.name?.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="w-10 h-12 rounded-md overflow-hidden bg-zinc-900 flex-shrink-0">
                      {a.primary_book_cover ? (
                        <img src={a.primary_book_cover} alt="" className="w-full h-full object-cover" />
                      ) : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-semibold truncate">{a.name}</p>
                      <p className="text-xs text-gray-400 truncate">{a.book_count} book{a.book_count === 1 ? '' : 's'}{a.primary_book_title ? ` · ${a.primary_book_title}` : ''}</p>
                    </div>
                    <Button
                      size="sm" variant={isSelected ? 'outline' : 'default'} disabled={isSelected || selected.length >= 6}
                      onClick={() => addAuthor(a)}
                      className={isSelected ? 'bg-white/5 border-white/10 text-gray-400' : 'bg-fuchsia-600 hover:bg-fuchsia-500 text-white'}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      {isSelected ? 'Featured' : 'Add'}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeaturedAuthorsPanel;
